/**
 * Opening a saved template in the designer (Slice DL).
 *
 * The designer normally works on a draft in localStorage. Viewing or
 * editing a template means loading a specific published version into it
 * instead, and the URL is what carries that intent — so a link to
 * "v1 of this template" survives a reload and the back button, the same
 * reason tabRoutes.ts gives each section its own path.
 *
 * View and edit differ in exactly two ways, both decided here: which
 * version is loaded (a named one, or the current one), and whether the
 * canvas accepts changes.
 */

export interface TemplateRequest {
  /** The template to load. An edit publishes its next version. */
  flowId: string;
  /**
   * A specific version to load. Absent means the current one, which is
   * what Edit uses: an edit is always based on where the template is now,
   * never on an older version someone happened to be looking at.
   */
  versionId?: string;
  /** True for View: the canvas is shown but accepts no changes. */
  readOnly: boolean;
}

/** Reads a designer link's own `?template=…` parameters. */
export function templateRequestFromSearch(search: string): TemplateRequest | null {
  const params = new URLSearchParams(search);
  const flowId = params.get("template");
  if (!flowId) return null;
  const versionId = params.get("version");
  return {
    flowId,
    ...(versionId ? { versionId } : {}),
    readOnly: params.get("view") === "1",
  };
}

/**
 * Builds the query string for such a link. Kept beside the reader so the
 * two cannot drift apart; the caller joins it to the designer's own path.
 */
export function templateSearch(request: TemplateRequest): string {
  const params = new URLSearchParams({ template: request.flowId });
  if (request.versionId) params.set("version", request.versionId);
  if (request.readOnly) params.set("view", "1");
  return "?" + params.toString();
}
